import { Avatar, Box, Card, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { mediaSourceUrl } from './utils'
import { IconSocialOrders } from '../../../icons/IconSocialOrders'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { createNotificationNavHandlers, getActorPathKey, getSocialOrderSlug } from './notificationNav'
import { ActorNameSpan, ProjectNameQuoted } from './notificationTypography'
import { notificationBodyWrapSx, notificationMessageClampSx } from './notificationTextClamp'

function restrictionLevelLabel(level: string): string {
  if (level === 'full') return 'доступ к соцзаказу ограничен'
  if (level === 'comments') return 'комментирование запрещено'
  return 'действуют ограничения'
}

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'social_order.restriction.applied' }>
}

export function SocialOrderRestrictionAppliedNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onSocialOrderClick, onUserClick } = createNotificationNavHandlers(navigate)
  const payload = notification.payload
  const socialOrder = payload.social_order
  const actor = payload.actor
  const restriction = payload.restriction
  const socialOrderTitle =
    typeof socialOrder?.title === 'string' && socialOrder.title.trim().length > 0
      ? socialOrder.title
      : 'соцзаказ'
  const actorName =
    typeof actor?.fullname === 'string' && actor.fullname.trim().length > 0 ? actor.fullname : 'Пользователь'
  const level = typeof restriction?.level === 'string' ? restriction.level : 'unknown'
  const reason = typeof restriction?.reason === 'string' ? restriction.reason.trim() : ''
  const socialOrderAvatarSrc = mediaSourceUrl(socialOrder?.image_url) ?? undefined
  const socialOrderSlug = getSocialOrderSlug(socialOrder)
  const actorPathKey = getActorPathKey(actor, notification.actor_user_id)

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onSocialOrderClick(e, socialOrderSlug)}
          sx={{ cursor: socialOrderSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={socialOrderTitle}
            avatarSrc={socialOrderAvatarSrc}
            fallbackIcon={<IconSocialOrders fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle="Ограничение доступа"
          />
        </Box>

        <Stack direction="row" gap={1.5} alignItems="flex-start" minWidth={0}>
          <Box
            onClick={actorPathKey ? (e) => onUserClick(e, actorPathKey) : undefined}
            sx={{ cursor: actorPathKey ? 'pointer' : 'default', flexShrink: 0 }}
          >
            <Avatar
              src={mediaSourceUrl(actor?.avatar_url) ?? undefined}
              alt={actorName}
              sx={{ width: 34, height: 34 }}
            />
          </Box>
          <Stack minWidth={0} gap={0.25}>
            <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
              <ActorNameSpan pathKey={actorPathKey} onUserClick={onUserClick}>
                {actorName}
              </ActorNameSpan>
              {' ограничил(а) ваш доступ к соцзаказу '}
              <ProjectNameQuoted name={socialOrderTitle} pathKey={socialOrderSlug} onNameClick={onSocialOrderClick} />
              {': '}
              {restrictionLevelLabel(level)}
            </Typography>
            {reason ? (
              <Typography variant="black-12-light" sx={notificationMessageClampSx}>
                {reason}
              </Typography>
            ) : null}
          </Stack>
        </Stack>

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
